import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8 } from "styled-components";
import React from "react";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { ComparisonTableItem } from "~/features/blocks/inventory/features-comparison-table/comparison-table-item";
import { takeFirst } from "~/features/app/utils";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type FeaturesComparisonTableItemValue = {
  feature?: string;
  featuredIn?: string;
};
export type FeaturesComparisonTableBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  hasTopDivider?: boolean | null;
  items?: FeaturesComparisonTableItemValue[] | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function FeaturesComparisonTableBlockView({
  anchor,
  hasTopDivider = true,
  items = [],
  mode = "light",
  sectionProps,
  theme = "white"
}: FeaturesComparisonTableBlockViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const entries = items ?? [];

  // in items, we have the featuredIn string, which is a comma-separated list.
  // we extract them and create a column on the right for each one.
  // @TODO: maybe find a better name for featuredIn and options
  const options = React.useMemo(() => {
    const _options = new Set<string>();
    entries.forEach(item => {
      if (item?.featuredIn) {
        item.featuredIn.split(",").forEach(option => {
          _options.add(option.trim());
        });
      }
    });
    return Array.from(_options);
  }, [entries]);
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "paddingBottom": "8rem"
  }, hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, themeStyle.background]}>
      <_StyledDiv $_css2={[hasTopDivider && {
      "borderBottomWidth": "1px"
    }, themeStyle.border]}>
        <Wrap mode={mode as Mode}>
          <_StyledDiv2 className="hide-scrollbar">
            <_StyledTable $_css3={[{
            "width": "100%",
            "borderCollapse": "collapse",
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }, themeStyle.border]}>
              <_StyledThead $_css4={[{
              "zIndex": "10"
            }, themeStyle.background]}>
                <tr>
                  <_StyledTh>
                    Feature
                  </_StyledTh>
                  {options.map((option: string) => <_StyledTh2 key={option} $_css5={[{
                  "whiteSpace": "nowrap",
                  "borderLeftWidth": "1px",
                  "padding": "2.4rem",
                  "textAlign": "left",
                  "fontSize": "1.6rem",
                  "lineHeight": "6px !important",
                  "fontWeight": "500",
                  "@media (min-width: 1024px)": {
                    "fontSize": "1.8rem",
                    "lineHeight": "1.5",
                    "letterSpacing": "0.018rem",
                    "fontWeight": "500"
                  }
                }, themeStyle.border]}>
                      {option}
                    </_StyledTh2>)}
                </tr>
              </_StyledThead>
              <_StyledTbody $_css6={[{
              "zIndex": "10"
            }, themeStyle.background]}>
                {entries.map((item, idx) => <ComparisonTableItem key={idx} mode={mode as Mode} theme={theme as Theme} options={options} {...item} />)}
              </_StyledTbody>
            </_StyledTable>
          </_StyledDiv2>
        </Wrap>
      </_StyledDiv>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-v3ywq-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-v3ywq-1"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-v3ywq-2"
})({
  "position": "relative",
  "overflowX": "auto",
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledTable = _styled4("table").withConfig({
  componentId: "sc-sf-v3ywq-3"
})`${p => p.$_css3}`;
var _StyledThead = _styled5("thead").withConfig({
  componentId: "sc-sf-v3ywq-4"
})`${p => p.$_css4}`;
var _StyledTh = _styled6("th").withConfig({
  componentId: "sc-sf-v3ywq-5"
})({
  "whiteSpace": "nowrap",
  "padding": "2.4rem",
  "textAlign": "left",
  "fontSize": "1.6rem",
  "lineHeight": "6px !important",
  "fontWeight": "500",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "500"
  }
});
var _StyledTh2 = _styled7("th").withConfig({
  componentId: "sc-sf-v3ywq-6"
})`${p => p.$_css5}`;
var _StyledTbody = /*#__PURE__*/_styled8("tbody").withConfig({
  displayName: "features-comparison-table___StyledTbody",
  componentId: "sc-16lvepd-0"
})(["", ""], p => p.$_css6);